import { RecordFields, RecordList } from "@/components/common/RecordList";
import { RecordSortControl } from "@/components/common/RecordSortControl";
import {
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  JoinInner as CategoryMappingIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  ClickAwayListener,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
  AdminEmptyState,
  AdminFilterPanel,
  PageShell,
  AdminTableBodyState,
  AdminTablePanel,
} from "@/components/common";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import { useLocalizedTablePagination } from "@/hooks/useLocalizedTablePagination";
import { useTableSorting } from "@/hooks/useTableSorting";
import { Customer, CustomerService } from "@/services/features/CustomerService";
import {
  isCustomerCategoryRowsPerPage,
  readCustomerCategoryTableSelection,
  writeCustomerCategoryTableSelection,
} from "@/utils/customerCategoryTableSelectionSession";
import { matchesCategorySearch } from "@/utils/categorySearch";
import { formatStoreDate, getBrowserTimezone } from "@/utils/storeTime";
import {
  CraveCategory,
  CraveCategoryService,
  CraveCategoryUpdateRequest,
} from "@/services/features/CraveCategoryService";

import { useCategoryViewFilters } from "./CategoryViewFiltersContext";

interface CustomerOption {
  id: number;
  label: string;
}

type CategorySortColumn = "id" | "name" | "primaryMapping" | "secondaryMapping" | "createdAt";

interface CategoryMappingProps {
  embedded?: boolean;
}

const CategoryMapping: React.FC<CategoryMappingProps> = ({ embedded = false }) => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const locale = i18n.resolvedLanguage || i18n.language;
  const browserTimezone = getBrowserTimezone();
  const theme = useTheme();
  const localizedTablePagination = useLocalizedTablePagination();
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [craveCategories, setCraveCategories] = useState<CraveCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCraveCategoriesLoading, setIsCraveCategoriesLoading] = useState(false);
  const [editingPrimaryCategory, setEditingPrimaryCategory] = useState<number | null>(null);
  const [editingSecondaryCategory, setEditingSecondaryCategory] = useState<number | null>(null);
  const [dropdownValue, setDropdownValue] = useState<string>("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const sharedFilters = useCategoryViewFilters();
  const [localMatchWholeWords, setLocalMatchWholeWords] = useState(false);
  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const matchWholeWords = sharedFilters?.matchWholeWords ?? localMatchWholeWords;
  const setMatchWholeWords = sharedFilters?.setMatchWholeWords ?? setLocalMatchWholeWords;
  const searchQuery = sharedFilters?.searchQuery ?? localSearchQuery;
  const setSearchQuery = sharedFilters?.setSearchQuery ?? setLocalSearchQuery;

  const customerService = CustomerService.getInstance();
  const craveCategoryService = CraveCategoryService.getInstance();

  useEffect(() => {
    document.title = t("management.category.categoryMapping.documentTitle");
  }, [t]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    const sharedSelection = readCustomerCategoryTableSelection();
    const customerIdParam = searchParams.get("customerId");
    const customerId =
      customerIdParam === null ? sharedSelection?.customerId : Number(customerIdParam);
    const customer = customers.find((item) => item.id === customerId);
    const pageParam = Number(searchParams.get("page"));
    const rowsPerPageParam = Number(searchParams.get("rowsPerPage"));
    const nextPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam - 1 : 0;
    const nextRowsPerPage = isCustomerCategoryRowsPerPage(rowsPerPageParam)
      ? rowsPerPageParam
      : (sharedSelection?.rowsPerPage ?? 50);

    if ((selectedCustomer?.id ?? null) !== (customer?.id ?? null)) {
      setSelectedCustomer(customer ? { id: customer.id, label: customer.name } : null);
    }

    setPage(nextPage);
    setRowsPerPage(nextRowsPerPage);
    const nextSearchParams = new URLSearchParams(searchParams);
    if (customers.length > 0) {
      if (customer) {
        nextSearchParams.set("customerId", String(customer.id));
      } else {
        nextSearchParams.delete("customerId");
      }
    }
    nextSearchParams.set("page", String(nextPage + 1));
    nextSearchParams.set("rowsPerPage", String(nextRowsPerPage));

    if (nextSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(nextSearchParams, { replace: true });
    }

    if (customers.length > 0) {
      writeCustomerCategoryTableSelection({
        customerId: customer?.id ?? null,
        rowsPerPage: nextRowsPerPage,
      });
    }
  }, [customers, searchParams, selectedCustomer?.id]);

  // Fetch crave categories when selected customer changes
  useEffect(() => {
    if (selectedCustomer) {
      fetchCraveCategories(selectedCustomer.id);
    } else {
      setCraveCategories([]);
    }
  }, [selectedCustomer]);

  const fetchCustomers = async () => {
    setIsLoading(true);
    try {
      const response = await customerService.getAllCustomers();
      if (response.success && response.data) {
        setCustomers(response.data);
      } else {
        console.error("Failed to fetch customers:", response.error);
        setCustomers([]);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCraveCategories = async (customerId: number) => {
    setIsCraveCategoriesLoading(true);
    try {
      const response = await craveCategoryService.getByCustomerId(customerId);
      if (response.success && response.data) {
        setCraveCategories(response.data);
      } else {
        console.error("Failed to fetch crave categories:", response.error);
        setCraveCategories([]);
      }
    } catch (error) {
      console.error("Error fetching crave categories:", error);
      setCraveCategories([]);
    } finally {
      setIsCraveCategoriesLoading(false);
    }
  };

  const handleCustomerChange = (_event: any, value: CustomerOption | null) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    if (value) {
      nextSearchParams.set("customerId", String(value.id));
    } else {
      nextSearchParams.delete("customerId");
    }
    nextSearchParams.set("page", "1");
    nextSearchParams.set("rowsPerPage", String(rowsPerPage));
    setSearchParams(nextSearchParams);
    setSelectedCustomer(value);
    writeCustomerCategoryTableSelection({ customerId: value?.id ?? null, rowsPerPage });
    // Clear previous categories when customer changes
    setCraveCategories([]);
    // Clear any editing state
    setEditingPrimaryCategory(null);
    setEditingSecondaryCategory(null);
    setDropdownValue("");
    setPage(0);
  };

  const handlePageChange = (nextPage: number) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("page", String(nextPage + 1));
    nextSearchParams.set("rowsPerPage", String(rowsPerPage));
    setSearchParams(nextSearchParams);
    setPage(nextPage);
  };

  const handleRowsPerPageChange = (nextRowsPerPage: number) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("page", "1");
    nextSearchParams.set("rowsPerPage", String(nextRowsPerPage));
    setSearchParams(nextSearchParams);
    setRowsPerPage(nextRowsPerPage);
    writeCustomerCategoryTableSelection({
      customerId: selectedCustomer?.id ?? null,
      rowsPerPage: nextRowsPerPage,
    });
    setPage(0);
  };

  const handleCellClick = (categoryId: number, field: "primary" | "secondary") => {
    const category = craveCategories.find((item) => item.id === categoryId);
    const mappingId = field === "primary" ? category?.primaryMapping : category?.secondaryMapping;

    if (field === "primary") {
      setEditingPrimaryCategory(categoryId);
    } else {
      setEditingSecondaryCategory(categoryId);
    }
    setDropdownValue(getCategoryNameById(mappingId));
  };

  const saveMapping = async (
    categoryId: number,
    field: "primary" | "secondary",
    mappingId: number | null,
  ) => {
    try {
      const currentCategory = craveCategories.find((cat) => cat.id === categoryId);
      if (!currentCategory) return;

      const updateRequest: CraveCategoryUpdateRequest = {
        id: currentCategory.id,
        name: currentCategory.name,
        primaryMapping: currentCategory.primaryMapping,
        secondaryMapping: currentCategory.secondaryMapping,
      };

      if (field === "primary") {
        updateRequest.primaryMapping = mappingId;
      } else {
        updateRequest.secondaryMapping = mappingId;
      }

      const response = await craveCategoryService.update(updateRequest);

      if (response.success) {
        // Update the local state with the new mapping value
        setCraveCategories((prev) =>
          prev.map((cat) => {
            if (cat.id === categoryId) {
              return {
                ...cat,
                [field === "primary" ? "primaryMapping" : "secondaryMapping"]: mappingId,
              };
            }
            return cat;
          }),
        );
      } else {
        console.error("Failed to update crave category:", response.error);
      }
    } catch (error) {
      console.error("Error updating crave category:", error);
    } finally {
      setEditingPrimaryCategory(null);
      setEditingSecondaryCategory(null);
      setDropdownValue("");
    }
  };

  const handleDropdownChange = async (_event: any, value: string | null) => {
    if (!value) return;

    const categoryId = editingPrimaryCategory || editingSecondaryCategory;
    const field = editingPrimaryCategory !== null ? "primary" : "secondary";
    const selectedCategory = craveCategories.find((cat) => cat.name === value);

    if (!categoryId || !selectedCategory) return;

    await saveMapping(categoryId, field, selectedCategory.id);
  };

  const handleDropdownClose = () => {
    setEditingPrimaryCategory(null);
    setEditingSecondaryCategory(null);
    setDropdownValue("");
  };

  const getCategoryNameById = (categoryId: number | null | undefined): string => {
    const category = craveCategories.find((cat) => cat.id === categoryId);
    return category?.name || "";
  };

  const categoryNameSx = {
    display: "block",
    maxWidth: "100%",
    overflowX: "auto",
    scrollbarWidth: "none",
    whiteSpace: "nowrap",
    "&::-webkit-scrollbar": { display: "none" },
  } as const;

  const customerOptions: CustomerOption[] = customers.map((customer) => ({
    id: customer.id,
    label: customer.name,
  }));
  const filteredCategories = craveCategories.filter((category) =>
    [
      category.name,
      getCategoryNameById(category.primaryMapping),
      getCategoryNameById(category.secondaryMapping),
    ].some((value) => matchesCategorySearch(value, searchQuery, matchWholeWords)),
  );
  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting(
    filteredCategories,
    (category, column) => {
      switch (column) {
        case "id":
          return category.id;
        case "name":
          return category.name;
        case "primaryMapping":
          return getCategoryNameById(category.primaryMapping);
        case "secondaryMapping":
          return getCategoryNameById(category.secondaryMapping);
        case "createdAt":
          return new Date(category.createdAt).getTime();
      }
    },
    "id" as CategorySortColumn,
    locale,
  );

  const paginatedItems = sortedItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const renderPagination = () => (
    <TablePagination
      component="div"
      count={sortedItems.length}
      page={page}
      onPageChange={(_event, nextPage) => handlePageChange(nextPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(event) => {
        handleRowsPerPageChange(Number(event.target.value));
      }}
      rowsPerPageOptions={[25, 50, 100, 250]}
      {...localizedTablePagination}
    />
  );
  const renderTableHeader = () => (
    <Box>
      <Box sx={{ minWidth: { xs: "100%", sm: 280 } }}>
        <TextField
          fullWidth
          label={t("management.category.categoryMapping.search.label")}
          placeholder={t("management.category.categoryMapping.search.placeholder")}
          value={searchQuery}
          onChange={(event) => {
            setSearchQuery(event.target.value);
            handlePageChange(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={t("common.clear")}
                  onClick={() => {
                    setSearchQuery("");
                    handlePageChange(0);
                  }}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: { xs: "flex-start", lg: "center" },
          gap: 1,
          mt: 1,
        }}
      >
        <FormControlLabel
          sx={{ color: "text.primary" }}
          control={
            <Switch
              checked={matchWholeWords}
              onChange={(event) => {
                setMatchWholeWords(event.target.checked);
                handlePageChange(0);
              }}
            />
          }
          label={t("management.category.categoryMapping.controls.matchWholeWords")}
        />
        <Box sx={{ alignSelf: { xs: "flex-end", lg: "auto" }, marginInlineStart: { lg: "auto" } }}>
          {renderPagination()}
        </Box>
      </Box>
    </Box>
  );

  const content = (
    <>
      <AdminFilterPanel>
        <MuiAutocompleteMultiSelect
          label={t("management.category.categoryMapping.fields.selectCustomer")}
          options={customerOptions}
          value={selectedCustomer ? [selectedCustomer] : []}
          onChange={(next) => handleCustomerChange(null, next[0] ?? null)}
          getOptionLabel={(option) => option.label}
          getOptionKey={(option) => option.id}
          loading={isLoading}
          disabled={isLoading}
        />
      </AdminFilterPanel>

      <AdminTablePanel
        header={renderTableHeader()}
        footer={renderPagination()}
        tableSx={{ width: "100%", minWidth: 960, tableLayout: "fixed" }}

        mobileContent={
          <Box
            sx={{
              minWidth: 0,
              "& .MuiTypography-root": {
                maxWidth: "100%",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              },
            }}
          >
            <RecordSortControl
              value={sortColumn}
              direction={sortDirection}
              onSort={(column) => {
                handleSort(column);
                handlePageChange(0);
              }}
              options={[
                {
                  value: "id" as const,
                  label: t("management.category.categoryMapping.columns.id"),
                },
                {
                  value: "name" as const,
                  label: t("management.category.categoryMapping.columns.name"),
                },
                {
                  value: "primaryMapping" as const,
                  label: t("management.category.categoryMapping.columns.primaryMapping"),
                },
                {
                  value: "secondaryMapping" as const,
                  label: t("management.category.categoryMapping.columns.secondaryMapping"),
                },
                {
                  value: "createdAt" as const,
                  label: t("management.category.categoryMapping.columns.createdAt"),
                },
              ]}
            />
            <RecordList
              items={paginatedItems}
              getItemId={(category) => category.id}
              loading={isCraveCategoriesLoading}
              resetKey={JSON.stringify([
                sortColumn,
                sortDirection,
                ...paginatedItems.map((category) => category.id),
              ])}
              renderTitle={(category) => {
                return (
                  <>
                    <Typography variant="body2" fontWeight="medium" sx={categoryNameSx}>
                      {category.name}
                    </Typography>
                  </>
                );
              }}
              renderSummary={(category) => {
                return (
                  <RecordFields
                    fields={[
                      {
                        label: <>{t("management.category.categoryMapping.columns.id")}</>,
                        value: (
                          <>
                            <Typography variant="body2">{category.id}</Typography>
                          </>
                        ),
                      },
                    ]}
                  />
                );
              }}
              renderDetails={(category) => {
                return (
                  <RecordFields
                    fields={[
                      {
                        label: (
                          <>{t("management.category.categoryMapping.columns.primaryMapping")}</>
                        ),
                        value: (
                          <Box
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              if (editingPrimaryCategory !== category.id) {
                                handleCellClick(category.id, "primary");
                              }
                            }}
                            onKeyDown={(event) => {
                              if (
                                editingPrimaryCategory !== category.id &&
                                (event.key === "Enter" || event.key === " ")
                              ) {
                                event.preventDefault();
                                handleCellClick(category.id, "primary");
                              }
                            }}
                            sx={{ minWidth: 0, overflowWrap: "anywhere" }}
                          >
                            {editingPrimaryCategory === category.id ? (
                              <ClickAwayListener onClickAway={handleDropdownClose}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    minWidth: 0,
                                    width: "100%",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Autocomplete
                                    size="small"
                                    value={dropdownValue}
                                    onChange={handleDropdownChange}
                                    onInputChange={(event, newInputValue) => {
                                      setDropdownValue(newInputValue);
                                    }}
                                    options={craveCategories.map((cat) => cat.name)}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        variant="outlined"
                                        placeholder={t(
                                          "management.category.categoryMapping.placeholders.searchCategories",
                                        )}
                                        size="small"
                                        onBlur={handleDropdownClose}
                                      />
                                    )}
                                    sx={{
                                      minWidth: 0,
                                      flex: 1,
                                      "& .MuiOutlinedInput-root": { bgcolor: "background.paper" },
                                      "& .MuiAutocomplete-input": (theme) => ({
                                        fontSize: theme.typography.body2.fontSize,
                                        lineHeight: theme.typography.body2.lineHeight,
                                      }),
                                    }}
                                  />
                                  <Tooltip title={t("common.clear")}>
                                    <IconButton
                                      size="small"
                                      aria-label={t("common.clear")}
                                      onMouseDown={(event) => event.preventDefault()}
                                      onClick={() => void saveMapping(category.id, "primary", null)}
                                      sx={{
                                        color: "error.main",
                                        "&:hover": { color: "error.dark", bgcolor: "action.hover" },
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </ClickAwayListener>
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  className="editable-cell-text"
                                  color="text.primary"
                                  sx={categoryNameSx}
                                >
                                  {category.primaryMapping
                                    ? getCategoryNameById(category.primaryMapping)
                                    : "-"}
                                </Typography>
                                <Box
                                  component="span"
                                  className="editable-cell-icon"
                                  sx={{ display: "inline-flex", p: "5px", color: "text.primary" }}
                                >
                                  <EditIcon fontSize="small" />
                                </Box>
                              </Box>
                            )}
                          </Box>
                        ),
                      },
                      {
                        label: (
                          <>{t("management.category.categoryMapping.columns.secondaryMapping")}</>
                        ),
                        value: (
                          <Box
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              if (editingSecondaryCategory !== category.id) {
                                handleCellClick(category.id, "secondary");
                              }
                            }}
                            onKeyDown={(event) => {
                              if (
                                editingSecondaryCategory !== category.id &&
                                (event.key === "Enter" || event.key === " ")
                              ) {
                                event.preventDefault();
                                handleCellClick(category.id, "secondary");
                              }
                            }}
                            sx={{ minWidth: 0, overflowWrap: "anywhere" }}
                          >
                            {editingSecondaryCategory === category.id ? (
                              <ClickAwayListener onClickAway={handleDropdownClose}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    minWidth: 0,
                                    width: "100%",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Autocomplete
                                    size="small"
                                    value={dropdownValue}
                                    onChange={handleDropdownChange}
                                    onInputChange={(event, newInputValue) => {
                                      setDropdownValue(newInputValue);
                                    }}
                                    options={craveCategories.map((cat) => cat.name)}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        variant="outlined"
                                        placeholder={t(
                                          "management.category.categoryMapping.placeholders.searchCategories",
                                        )}
                                        size="small"
                                        onBlur={handleDropdownClose}
                                      />
                                    )}
                                    sx={{
                                      minWidth: 0,
                                      flex: 1,
                                      "& .MuiOutlinedInput-root": { bgcolor: "background.paper" },
                                      "& .MuiAutocomplete-input": (theme) => ({
                                        fontSize: theme.typography.body2.fontSize,
                                        lineHeight: theme.typography.body2.lineHeight,
                                      }),
                                    }}
                                  />
                                  <Tooltip title={t("common.clear")}>
                                    <IconButton
                                      size="small"
                                      aria-label={t("common.clear")}
                                      onMouseDown={(event) => event.preventDefault()}
                                      onClick={() =>
                                        void saveMapping(category.id, "secondary", null)
                                      }
                                      sx={{
                                        color: "error.main",
                                        "&:hover": { color: "error.dark", bgcolor: "action.hover" },
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </ClickAwayListener>
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  className="editable-cell-text"
                                  color="text.primary"
                                  sx={categoryNameSx}
                                >
                                  {category.secondaryMapping
                                    ? getCategoryNameById(category.secondaryMapping)
                                    : "-"}
                                </Typography>
                                <Box
                                  component="span"
                                  className="editable-cell-icon"
                                  sx={{ display: "inline-flex", p: "5px", color: "text.primary" }}
                                >
                                  <EditIcon fontSize="small" />
                                </Box>
                              </Box>
                            )}
                          </Box>
                        ),
                      },
                      {
                        label: <>{t("management.category.categoryMapping.columns.createdAt")}</>,
                        value: (
                          <>
                            <Typography variant="body2">
                              {formatStoreDate(category.createdAt, browserTimezone, locale)}
                            </Typography>
                          </>
                        ),
                      },
                    ]}
                  />
                );
              }}

              emptyState={
                <AdminEmptyState
                  icon={<CategoryMappingIcon sx={{ fontSize: 40 }} />}
                  text={
                    selectedCustomer
                      ? t("management.category.categoryMapping.empty.noCraveCategories")
                      : t("management.category.categoryMapping.empty.selectCustomer")
                  }
                />
              }
            />
          </Box>
        }
      >
        <TableHead>
          <TableRow>
            <SortableTableCell
              activeColumn={sortColumn}
              column="id"
              direction={sortDirection}
              onSort={(column) => {
                handleSort(column);
                handlePageChange(0);
              }}
              sx={{ width: 80, minWidth: 80, whiteSpace: "nowrap" }}
            >
              {t("management.category.categoryMapping.columns.id")}
            </SortableTableCell>
            <SortableTableCell
              activeColumn={sortColumn}
              column="name"
              direction={sortDirection}
              onSort={(column) => {
                handleSort(column);
                handlePageChange(0);
              }}
              sx={{ width: "25%", maxWidth: "25%" }}
            >
              {t("management.category.categoryMapping.columns.name")}
            </SortableTableCell>
            <SortableTableCell
              activeColumn={sortColumn}
              column="primaryMapping"
              direction={sortDirection}
              onSort={(column) => {
                handleSort(column);
                handlePageChange(0);
              }}
              sx={{ width: "25%", minWidth: "25%" }}
            >
              {t("management.category.categoryMapping.columns.primaryMapping")}
            </SortableTableCell>
            <SortableTableCell
              activeColumn={sortColumn}
              column="secondaryMapping"
              direction={sortDirection}
              onSort={(column) => {
                handleSort(column);
                handlePageChange(0);
              }}
              sx={{ width: "25%", minWidth: "25%" }}
            >
              {t("management.category.categoryMapping.columns.secondaryMapping")}
            </SortableTableCell>
            <SortableTableCell
              activeColumn={sortColumn}
              column="createdAt"
              direction={sortDirection}
              onSort={(column) => {
                handleSort(column);
                handlePageChange(0);
              }}
              sx={{ width: 160, minWidth: 160, whiteSpace: "nowrap" }}
            >
              {t("management.category.categoryMapping.columns.createdAt")}
            </SortableTableCell>
          </TableRow>
        </TableHead>
        <AdminTableBodyState
          items={paginatedItems}
          isLoading={isCraveCategoriesLoading}
          colSpan={5}
          loadingLabel={t("common.loading")}
          emptyState={
            <AdminEmptyState
              icon={<CategoryMappingIcon sx={{ fontSize: 40 }} />}
              text={
                selectedCustomer
                  ? t("management.category.categoryMapping.empty.noCraveCategories")
                  : t("management.category.categoryMapping.empty.selectCustomer")
              }
            />
          }
        >
          {(category) => (
            <TableRow key={category.id} hover>
              <TableCell sx={{ width: 80, minWidth: 80, whiteSpace: "nowrap" }}>
                <Typography variant="body2">{category.id}</Typography>
              </TableCell>
              <TableCell sx={{ width: "25%", maxWidth: "25%" }}>
                <Typography variant="body2" fontWeight="medium" sx={categoryNameSx}>
                  {category.name}
                </Typography>
              </TableCell>
              <TableCell
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (editingPrimaryCategory !== category.id) {
                    handleCellClick(category.id, "primary");
                  }
                }}
                onKeyDown={(event) => {
                  if (
                    editingPrimaryCategory !== category.id &&
                    (event.key === "Enter" || event.key === " ")
                  ) {
                    event.preventDefault();
                    handleCellClick(category.id, "primary");
                  }
                }}
                sx={{
                  width: "25%",
                  minWidth: "25%",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "action.hover",
                    "& .editable-cell-icon, & .editable-cell-text": { color: "primary.main" },
                  },
                }}
              >
                {editingPrimaryCategory === category.id ? (
                  <ClickAwayListener onClickAway={handleDropdownClose}>
                    <Box
                      sx={{
                        display: "flex",
                        minWidth: 0,
                        width: "100%",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Autocomplete
                        size="small"
                        value={dropdownValue}
                        onChange={handleDropdownChange}
                        onInputChange={(event, newInputValue) => {
                          setDropdownValue(newInputValue);
                        }}
                        options={craveCategories.map((cat) => cat.name)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder={t(
                              "management.category.categoryMapping.placeholders.searchCategories",
                            )}
                            size="small"
                            onBlur={handleDropdownClose}
                          />
                        )}
                        sx={{
                          minWidth: 0,
                          flex: 1,
                          "& .MuiOutlinedInput-root": { bgcolor: "background.paper" },
                          "& .MuiAutocomplete-input": (theme) => ({
                            fontSize: theme.typography.body2.fontSize,
                            lineHeight: theme.typography.body2.lineHeight,
                          }),
                        }}
                      />
                      <Tooltip title={t("common.clear")}>
                        <IconButton
                          size="small"
                          aria-label={t("common.clear")}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => void saveMapping(category.id, "primary", null)}
                          sx={{
                            color: "error.main",
                            "&:hover": { color: "error.dark", bgcolor: "action.hover" },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ClickAwayListener>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      className="editable-cell-text"
                      color="text.primary"
                      sx={categoryNameSx}
                    >
                      {category.primaryMapping ? getCategoryNameById(category.primaryMapping) : "-"}
                    </Typography>
                    <Box
                      component="span"
                      className="editable-cell-icon"
                      sx={{ display: "inline-flex", p: "5px", color: "text.primary" }}
                    >
                      <EditIcon fontSize="small" />
                    </Box>
                  </Box>
                )}
              </TableCell>
              <TableCell
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (editingSecondaryCategory !== category.id) {
                    handleCellClick(category.id, "secondary");
                  }
                }}
                onKeyDown={(event) => {
                  if (
                    editingSecondaryCategory !== category.id &&
                    (event.key === "Enter" || event.key === " ")
                  ) {
                    event.preventDefault();
                    handleCellClick(category.id, "secondary");
                  }
                }}
                sx={{
                  width: "25%",
                  minWidth: "25%",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "action.hover",
                    "& .editable-cell-icon, & .editable-cell-text": { color: "primary.main" },
                  },
                }}
              >
                {editingSecondaryCategory === category.id ? (
                  <ClickAwayListener onClickAway={handleDropdownClose}>
                    <Box
                      sx={{
                        display: "flex",
                        minWidth: 0,
                        width: "100%",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Autocomplete
                        size="small"
                        value={dropdownValue}
                        onChange={handleDropdownChange}
                        onInputChange={(event, newInputValue) => {
                          setDropdownValue(newInputValue);
                        }}
                        options={craveCategories.map((cat) => cat.name)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder={t(
                              "management.category.categoryMapping.placeholders.searchCategories",
                            )}
                            size="small"
                            onBlur={handleDropdownClose}
                          />
                        )}
                        sx={{
                          minWidth: 0,
                          flex: 1,
                          "& .MuiOutlinedInput-root": { bgcolor: "background.paper" },
                          "& .MuiAutocomplete-input": (theme) => ({
                            fontSize: theme.typography.body2.fontSize,
                            lineHeight: theme.typography.body2.lineHeight,
                          }),
                        }}
                      />
                      <Tooltip title={t("common.clear")}>
                        <IconButton
                          size="small"
                          aria-label={t("common.clear")}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => void saveMapping(category.id, "secondary", null)}
                          sx={{
                            color: "error.main",
                            "&:hover": { color: "error.dark", bgcolor: "action.hover" },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ClickAwayListener>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      className="editable-cell-text"
                      color="text.primary"
                      sx={categoryNameSx}
                    >
                      {category.secondaryMapping
                        ? getCategoryNameById(category.secondaryMapping)
                        : "-"}
                    </Typography>
                    <Box
                      component="span"
                      className="editable-cell-icon"
                      sx={{ display: "inline-flex", p: "5px", color: "text.primary" }}
                    >
                      <EditIcon fontSize="small" />
                    </Box>
                  </Box>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatStoreDate(category.createdAt, browserTimezone, locale)}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </AdminTableBodyState>
      </AdminTablePanel>
    </>
  );

  if (embedded) return content;

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("management.category.categoryMapping.title")}
      icon={<CategoryMappingIcon />}
    >
      {content}
    </PageShell>
  );
};

export default CategoryMapping;
